import { Fragment, useState } from 'react';
import { Chip } from '@affdice/portal-kit';

import type { PlatformApi } from '../../client.ts';
import { t } from '../../messages.ts';
import { useAbilities } from '../abilities.tsx';
import { brandActions, brandStanding, brandStatusAction, type LifecycleMode } from '../entity-actions.ts';
import { DirectEditDialog } from '../DirectEditDialog.tsx';
import { LoginAsPickerButton } from '../login-as/LoginAs.tsx';
import { brandOffersLoginAs } from '../login-as/login-as-model.ts';
import { LifecycleDialog } from '../LifecycleDialog.tsx';
import { ActionBar } from '../shared.tsx';
import { SetBrandColourModal } from './SetBrandColourModal.tsx';
import { SetBrandLogoModal } from './SetBrandLogoModal.tsx';

export interface BrandActionsBrand {
  readonly brand_id: string;
  readonly display_name: string;
  readonly status: string;
  readonly theme_accent: string | null;
  readonly logo_url?: string | null | undefined;
  readonly soft_deleted_at?: string | null | undefined;
  readonly purged_at?: string | null | undefined;
  /** The operator's lifecycle state, where the opener knows it. */
  readonly operator_state?: string | undefined;
}

/**
 * The actions panel of one brand: log in as one of its people, set its
 * colour, and delete, restore or purge it. Which show follows `brandActions` — whether the brand is live, deleted
 * or purged, and what the viewer holds. Proposing a change to its settings is
 * a form of its own further down the popup, gated by the same model.
 */
export function BrandActions({
  api,
  operatorId,
  brand,
  onColour,
  onLogo,
  onStatus,
  onLifecycle,
}: {
  api: PlatformApi;
  operatorId: string;
  brand: BrandActionsBrand;
  /** The colour was saved. */
  onColour: (themeAccent: string | null) => void;
  /** The logo was saved or removed; the new path, or null. */
  onLogo: (logoUrl: string | null) => void;
  /** Switched on or off (3.9d). */
  onStatus: (status: 'ACTIVE' | 'INACTIVE') => void;
  /** Deleted, restored or purged: the brand's standing changed, so whoever lists it must re-read. */
  onLifecycle: (act: LifecycleMode) => void;
}) {
  const { ready, can } = useAbilities();
  const [colouring, setColouring] = useState(false);
  const [logoing, setLogoing] = useState(false);
  const [lifecycle, setLifecycle] = useState<LifecycleMode | null>(null);
  const [switching, setSwitching] = useState(false);
  const standing = brandStanding(brand);
  const actions = brandActions(standing, can);
  const statusAct = brandStatusAction(standing, brand.status, can);
  const loginAs = brandOffersLoginAs(standing, brand.status, brand.operator_state, can);

  if (!ready) return null;

  const LABEL: Record<LifecycleMode, string> = {
    delete: t('ent.life.action.delete'),
    restore: t('ent.life.action.restore'),
    purge: t('ent.life.action.purge'),
    'purge-now': t('ent.life.action.purgeNow'),
  };

  return (
    <>
      <ActionBar count={actions.length + (statusAct ? 1 : 0) + (loginAs ? 1 : 0)} purged={standing.purged} can={can}>
        {standing.purged ? <Chip tone="overdue">{t('ent.state.purged')}</Chip>
          : standing.deleted ? <Chip tone="neutral">{t('ent.state.deleted')}</Chip>
            : <Chip tone={brand.status === 'ACTIVE' ? 'locked' : 'neutral'}>{brand.status}</Chip>}
        {statusAct ? (
          <button
            type="button"
            className={statusAct === 'deactivate' ? 'danger-quiet' : 'copy'}
            onClick={() => setSwitching(true)}
          >
            {statusAct === 'deactivate' ? t('edit.brand.deactivate') : t('edit.brand.activate')}
          </button>
        ) : null}
        {/* The picker's title names the brand, so the operator's name is not read. */}
        {loginAs ? (
          <LoginAsPickerButton
            api={api}
            operator={{ id: operatorId, name: brand.display_name }}
            brand={{ id: brand.brand_id, name: brand.display_name }}
          />
        ) : null}
        {actions.map((a) =>
          a === 'propose-change' ? null : a === 'set-colour' ? (
            <Fragment key={a}>
              <button type="button" className="copy" onClick={() => setColouring(true)}>
                {t('brand.panel.setColour')}
              </button>
              {/* A logo is the same class of edit as the colour: it changes
                  nothing an affiliate is owed, so the same ability gates it. */}
              <button type="button" className="copy" onClick={() => setLogoing(true)}>
                {t('brand.panel.setLogo')}
              </button>
            </Fragment>
          ) : (
            <button key={a} type="button" className={a === 'restore' ? 'copy' : 'danger-quiet'} onClick={() => setLifecycle(a)}>
              {LABEL[a]}
            </button>
          ),
        )}
      </ActionBar>

      {colouring ? (
        <SetBrandColourModal
          api={api}
          operatorId={operatorId}
          brand={{ brand_id: brand.brand_id, display_name: brand.display_name, theme_accent: brand.theme_accent }}
          onClose={() => setColouring(false)}
          onSaved={(r) => {
            setColouring(false);
            onColour(r.theme_accent);
          }}
        />
      ) : null}

      {logoing ? (
        <SetBrandLogoModal
          api={api}
          brand={brand}
          onClose={() => setLogoing(false)}
          onChanged={onLogo}
        />
      ) : null}

      {switching && statusAct ? (
        <DirectEditDialog
          title={statusAct === 'deactivate'
            ? t('edit.brand.deactivateTitle', { name: brand.display_name })
            : t('edit.brand.activateTitle', { name: brand.display_name })}
          submitLabel={statusAct === 'deactivate' ? t('edit.brand.deactivate') : t('edit.brand.activate')}
          danger={statusAct === 'deactivate'}
          ready
          run={(reason) =>
            api.setBrandStatus(operatorId, brand.brand_id, statusAct === 'deactivate' ? 'INACTIVE' : 'ACTIVE', reason)}
          onClose={() => setSwitching(false)}
          onDone={() => {
            setSwitching(false);
            onStatus(statusAct === 'deactivate' ? 'INACTIVE' : 'ACTIVE');
          }}
        >
          <p>{statusAct === 'deactivate' ? t('edit.brand.deactivateNote') : t('edit.brand.activateNote')}</p>
        </DirectEditDialog>
      ) : null}

      {lifecycle ? (
        <LifecycleDialog
          mode={lifecycle}
          kind="brand"
          name={brand.display_name}
          needsName
          run={(body) =>
            api.brandDeletion(operatorId, brand.brand_id, lifecycle === 'purge-now' ? 'purge' : lifecycle, body)}
          onClose={() => setLifecycle(null)}
          onDone={() => {
            const act = lifecycle;
            setLifecycle(null);
            onLifecycle(act);
          }}
        />
      ) : null}
    </>
  );
}
